import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FilterItem, RangeSelector } from '@/components/molecules';
import { IFiltersListMobileCmp } from './IFiltersListMobileCmp';
import { ICmsTagsGroup } from '@/types/collections';
import { LinkBack, Typography } from '@/components/atoms';
import { useTranslation } from 'next-i18next';
import { capitalize, getLabel } from '@/utils/index';
import { RootState } from '@/store/store';
import { useSelector } from 'react-redux';

interface StyledProps {
  show?: boolean;
}

const RangeSelectorWrapper = styled.div`
  padding: 8px 0;
  margin-bottom: 24px;
`;

const FilterItemWrapper = styled.div`
  margin-bottom: 4px;
`;

const FiltersWrapper = styled.div`
  padding: 0 8px;
  height: calc(100% - 300px);
  overflow-y: auto;
  margin-bottom: 32px;
`;
const LinkBackWrapper = styled.div<StyledProps>`
  padding: 8px;
  display: ${props => (props.show ? 'block' : 'none')};
  color: ${props => props.theme.paletteDefault.primary.main};
`;

const Title = styled.div`
  color: ${props => props.theme.paletteDefault.primary.main};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const FiltersListMobile: React.FC<IFiltersListMobileCmp> = ({
  filters,
  filtersSelected,
  filterSelectHandler,
  removeFilterHandler,
  cmsCollectionTagsFilters,
}) => {
  const isB2b = process.env.NEXT_PUBLIC_B2B === '1';
  const { t } = useTranslation(['common', 'catalog']);
  const storeColors = useSelector(
    (state: RootState) => state.colorsReducer.colors
  );

  const currency = useSelector(
    (state: RootState) => state.currencyReducer.currency
  );
  const [list, setList] = useState<any[]>([]);
  const [shownFilter, setShownFilter] = useState<string | null>(null);

  const filterShownHandler = (filter: string) => {
    setShownFilter(filter);
  };

  const priceCustomRangeHandler = (priceValue: string): void => {
    filterSelectHandler({
      ...filtersSelected,
      ['price']: {
        slug: priceValue,
        label: getLabel(
          priceValue,
          cmsCollectionTagsFilters?.price || [],
          priceValue,
          'price',
          currency?.symbol || null
        ),
      },
    });
  };

  const remove = (filter: string) => {
    setShownFilter(null);
    removeFilterHandler(filter);
  };

  useEffect(() => {
    if (filters) {
      const list = getBrowseFilters(
        filters,
        filtersSelected,
        filterSelectHandler,
        filterShownHandler,
        remove,
        shownFilter,
        t,
        storeColors,
        priceCustomRangeHandler,
        isB2b
      );

      setList(list);
    }
  }, [shownFilter, filtersSelected, filters]);

  let shownFilterLabel;
  let title = '';
  if (shownFilter) {
    if (shownFilter.toLowerCase() === 'artisan' && isB2b) {
      shownFilterLabel = capitalize(t('artisanB2b'));
    } else {
      shownFilterLabel = capitalize(t(`catalog:filters.${shownFilter}`));
    }

    title = 'FILTER BY' + ` : ${shownFilterLabel.toUpperCase()}`;
  }

  return (
    <>
      <Title>
        <Typography component="h1" style={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </Title>
      <LinkBackWrapper show={shownFilter ? true : false}>
        <LinkBack
          label={'Back'}
          clickHandler={() => {
            setShownFilter(null);
          }}
        />
      </LinkBackWrapper>
      <FiltersWrapper className={'customWrapper'}>{list}</FiltersWrapper>
    </>
  );
};

export default FiltersListMobile;

const getBrowseFilters = (
  filters: ICmsTagsGroup | null,
  filtersSelected: { [k: string]: { slug: string; label: string } } | null,
  filterSelectHandler: (selected: {
    [k: string]: {
      slug: string;
      label: string;
    };
  }) => void,
  filterShownHandler: (filter: string) => void,
  removeFilterHandler: (filter: string) => void,
  shownFilter: string | null,
  t: any,
  colors: any,
  priceCustomRangeHandler: (priceValue: string) => void,
  isB2b: boolean
): any => {
  let cat: JSX.Element[] = [];
  let foundPriceRange = null;
  if (filters) {
    if (!shownFilter) {
      Object.keys(filters).forEach((filterName, index) => {
        if (filterName.toLowerCase() !== 'gender') {
          if (filters[filterName] && filters[filterName].length > 0) {
            let label;
            if (filterName.toLowerCase() === 'artisan' && isB2b) {
              label = capitalize(t('artisanB2b'));
            } else {
              label = capitalize(t(`catalog:filters.${filterName}`));
            }

            let filterItem = (
              <FilterItemWrapper key={`filter-${filterName}${index}`}>
                <FilterItem
                  label={label}
                  subLabel={
                    filtersSelected && filtersSelected[filterName]
                      ? filtersSelected[filterName].label
                      : ''
                  }
                  active={
                    filtersSelected && filtersSelected[filterName]
                      ? true
                      : false
                  }
                  clickHandler={() => {
                    filterShownHandler(filterName);
                  }}
                  hasChildren={true}
                  role="parent"
                  removeHandler={() => {
                    removeFilterHandler(filterName);
                  }}></FilterItem>
              </FilterItemWrapper>
            );
            cat.push(filterItem);
          }
        }
      });
    } else {
      if (filters[shownFilter]) {
        filters[shownFilter].forEach((filterValue, index) => {
          if (shownFilter === 'color') {
            let colorSlug = (
              filterValue?.label || filterValue.slug
            ).toLowerCase();

            let colorLabel = filterValue?.label || filterValue.slug;

            let bgColor = [colors[colorSlug]?.backgroundColor];

            let bgImageUrl =
              colors[colorSlug]?.backgroundImage?.fields?.file?.url;

            let filterItem = (
              <FilterItemWrapper key={`colorfilter-${colorSlug}${index}`}>
                <FilterItem
                  label={colorLabel} // TODO: check LABEL
                  active={
                    filtersSelected &&
                    filtersSelected['color'] &&
                    filtersSelected['color'].slug === colorSlug
                      ? true
                      : false
                  }
                  clickHandler={() => {
                    filterSelectHandler({
                      ...filtersSelected,
                      [shownFilter]: {
                        slug: colorSlug,
                        label: colorLabel,
                      },
                    });
                  }}
                  hasChildren={false}
                  removeHandler={() => {
                    removeFilterHandler(shownFilter);
                  }}
                  colorInfo={{ bgColor, bgImageUrl }}></FilterItem>
              </FilterItemWrapper>
            );
            cat.push(filterItem);
          } else {
            if (shownFilter === 'price') {
              if (
                filtersSelected &&
                filtersSelected['price'] &&
                filtersSelected['price'].slug === filterValue.slug
              ) {
                foundPriceRange = filterValue.slug;
              }
            }
            let filterItem = (
              <FilterItemWrapper key={`filter-${filterValue.slug}${index}`}>
                <FilterItem
                  label={filterValue?.label || filterValue.slug} // TODO: check LABEL
                  active={
                    filtersSelected &&
                    filtersSelected[shownFilter] &&
                    filtersSelected[shownFilter].slug === filterValue.slug
                      ? true
                      : false
                  }
                  clickHandler={() => {
                    filterSelectHandler({
                      ...filtersSelected,
                      [shownFilter]: {
                        slug: filterValue.slug,
                        label: filterValue?.label || filterValue.slug,
                      },
                    });
                  }}
                  hasChildren={false}
                  removeHandler={() => {
                    removeFilterHandler(shownFilter);
                  }}></FilterItem>
              </FilterItemWrapper>
            );
            cat.push(filterItem);
          }
        });
      }
    }
  }

  if (shownFilter === 'price') {
    let priceInitialRange = '-';
    if (!foundPriceRange) {
      priceInitialRange = filtersSelected?.price?.slug || '-';
    }

    return (
      <>
        {cat}
        <RangeSelectorWrapper>
          <RangeSelector
            inputRange={priceInitialRange}
            active={true}
            applyHandler={priceCustomRangeHandler}
            showButton={true}
            showBackgroundColors={true}
            resetHandler={() => {
              removeFilterHandler('price');
            }}
          />
        </RangeSelectorWrapper>
      </>
    );
  }
  return cat;
};
